import { useRef, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Controle segmentado do Design System (ADR-0065), o `.seg` do pacote: moldura
 * hairline única, divisória de 1px entre as opções e a opção ativa como
 * preenchimento em aço. É o controle do card "Casos do portal" na prancha 1B.
 *
 * Implementado como `radiogroup` de botões: uma escolha exclusiva entre poucas
 * opções visíveis, com as setas do teclado navegando o grupo. O pacote usa
 * `input[type=radio]` escondido com `:has()`; aqui o estado vem do React e a
 * semântica vem do ARIA, sem depender de suporte a `:has()`.
 */

export interface SegOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Nome acessível quando o rótulo visível não é texto. */
  ariaLabel?: string;
  disabled?: boolean;
  /**
   * `data-testid` da opção. O primeiro consumidor real (E54.F4) já era
   * identificado assim pelos testes e pelo smoke; sem o repasse, trocar o
   * controle pelo primitivo obrigaria a reescrever asserções que não têm nada
   * a ver com a mudança visual.
   */
  testId?: string;
}

export interface SegProps<T extends string> {
  /** `readonly` porque o primitivo só lê: uma lista const do consumidor serve. */
  options: readonly SegOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Nome acessível do grupo (i18n fica com o consumidor). */
  label: string;
  className?: string;
}

export function Seg<T extends string>({ options, value, onChange, label, className }: SegProps<T>) {
  const habilitadas = options.filter((o) => !o.disabled);
  const botoes = useRef(new Map<T, HTMLButtonElement>());

  function mover(atual: T, passo: 1 | -1) {
    if (habilitadas.length === 0) return;
    const i = habilitadas.findIndex((o) => o.value === atual);
    const proxima = habilitadas[(i + passo + habilitadas.length) % habilitadas.length];
    if (!proxima) return;
    onChange(proxima.value);
    // O roving tabindex move a **parada de tabulação** para a opção nova; sem
    // mover o foco junto, ele fica no botão antigo — que acabou de virar
    // `tabIndex={-1}` —, o Tab seguinte pula a opção ativa e as setas continuam
    // calculando a partir do item errado.
    botoes.current.get(proxima.value)?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn('inline-flex border border-border', className)}
    >
      {options.map((opcao, indice) => {
        const ativa = opcao.value === value;
        return (
          <button
            key={opcao.value}
            ref={(elemento) => {
              if (elemento) botoes.current.set(opcao.value, elemento);
              else botoes.current.delete(opcao.value);
            }}
            type="button"
            role="radio"
            aria-checked={ativa}
            aria-label={opcao.ariaLabel}
            data-testid={opcao.testId}
            disabled={opcao.disabled}
            tabIndex={ativa ? 0 : -1}
            onClick={() => onChange(opcao.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'ArrowRight' || evento.key === 'ArrowDown') {
                evento.preventDefault();
                mover(opcao.value, 1);
              }
              if (evento.key === 'ArrowLeft' || evento.key === 'ArrowUp') {
                evento.preventDefault();
                mover(opcao.value, -1);
              }
            }}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-[7px] text-body-sm transition-colors duration-fast ease-sc focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-45',
              indice > 0 && 'border-l border-border',
              ativa
                ? 'bg-accent-600 text-fg-onInverse'
                : 'bg-transparent text-fg hover:bg-fg/[0.07]',
            )}
          >
            {opcao.label}
          </button>
        );
      })}
    </div>
  );
}
